import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

import { FormFields } from './FormFields';
import type { FormField } from './formModel';
import styles from './RecordPage.module.scss';

/**
 * SEO записи: то, что видят поисковики и соцсети. Поля — те же, что у
 * записи, и проверяются тем же кодом; сверху — как запись будет выглядеть в
 * выдаче, с подстановкой названия, если своего заголовка нет.
 */
export function SeoPanel({
  values,
  title,
  slug,
  errors,
  siteID,
  siteURL,
  onChange,
  onSlug,
}: {
  values: Record<string, unknown>;
  title: string;
  slug: string;
  errors: Record<string, string>;
  siteID: string;
  siteURL: string | undefined;
  onChange: (key: string, value: unknown) => void;
  onSlug: (slug: string) => void;
}) {
  const seoTitle =
    typeof values['title'] === 'string' && values['title'] !== '' ? values['title'] : title;
  const description = typeof values['description'] === 'string' ? values['description'] : '';
  const address = `${(siteURL ?? 'https://сайт').replace(/\/$/, '')}/${slug}`;

  return (
    <div className={styles['seo']}>
      <div className={styles['seo__preview']} aria-label="Как запись выглядит в поиске">
        <span className={styles['seo__url']}>{address}</span>
        <span className={styles['seo__title']}>{seoTitle === '' ? 'Без названия' : seoTitle}</span>
        <span className={styles['seo__description']}>
          {description === ''
            ? 'Описания нет — поисковик возьмёт кусок текста со страницы.'
            : description}
        </span>
        {values['noindex'] === true && (
          <span className={styles['seo__noindex']}>Скрыта от поисковиков</span>
        )}
      </div>

      <Field
        label="Адрес записи"
        hint="Латиница, цифры и дефисы: /o-kompanii. Меняется — старые ссылки перестанут работать."
      >
        <Input
          mono
          value={slug}
          spellCheck={false}
          placeholder="o-kompanii"
          onChange={(event) => {
            onSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
          }}
        />
      </Field>

      <FormFields
        fields={SEO_FIELDS}
        values={values}
        errors={errors}
        path="_seo."
        siteID={siteID}
        onChange={onChange}
      />
    </div>
  );
}

function field(patch: Partial<FormField> & Pick<FormField, 'key' | 'name' | 'type'>): FormField {
  return {
    id: `seo:${patch.key}`,
    required: false,
    hint: '',
    options: {},
    widget: '',
    rules: {},
    showIf: undefined,
    width: 'full',
    tab: '',
    subfields: [],
    translatable: false,
    ...patch,
  };
}

// Пределы — по тому, что поисковик показывает без обрезки; зеркало сервера
// (internal/content/seo.go).
const SEO_FIELDS: FormField[] = [
  field({
    key: 'title',
    name: 'Заголовок для поисковиков',
    type: 'string',
    hint: 'Пусто — возьмётся название записи.',
    rules: { max_length: 70 },
  }),
  field({
    key: 'description',
    name: 'Описание для поисковиков',
    type: 'text',
    hint: 'Две фразы о том, что на странице.',
    rules: { max_length: 160 },
  }),
  field({
    key: 'image',
    name: 'Картинка для соцсетей',
    type: 'image',
    hint: 'Видна, когда ссылкой делятся.',
  }),
  field({ key: 'noindex', name: 'Не показывать в поиске', type: 'boolean', widget: 'switch' }),
];
